import React, { memo, useMemo, useState, useCallback, useEffect } from "react";

// normaliza el nombre de la carpeta para usarlo en la clave
const slug = (s) =>
  (s || "sin-nombre")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-");

// ===== UI helpers =====
const RectBtn = memo(({ icon, children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 16px", // mismo tamaño que tenías
      background: "#ffffff",
      border: "1px solid #E6EDFF", // mismo color de borde
      borderRadius: 4, // como en el diseño (radio 4px)
      fontWeight: 700,
      cursor: "pointer",
      lineHeight: 1.1,
    }}
  >
    {icon ? <i className={`bi ${icon}`} /> : null}
    {children}
  </button>
));

const PrimaryBtn = memo(({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 16px", // MISMO QUE RectBtn
      background: "#0081FF",
      border: "1px solid #0081FF",
      borderRadius: 4, // MISMO QUE RectBtn
      color: "#ffffff",
      fontWeight: 700,
      cursor: "pointer",
      lineHeight: 1.1,
    }}
  >
    {children}
  </button>
));

const SearchBox = memo(
  ({ placeholder = "Buscar artículo", value = "", onChange }) => (
    <div
      style={{
        marginLeft: "auto",
        position: "relative",
        width: "clamp(220px, 28vw, 360px)",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <i
        className="bi bi-search"
        style={{
          position: "absolute",
          left: 10,
          top: "50%",
          transform: "translateY(-50%)",
          opacity: 0.6,
        }}
      />
      <input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px 10px 34px",
          border: "1px solid #E6EDFF",
          borderRadius: 8,
          outline: "none",
          background: "#fff",
          boxSizing: "border-box",
        }}
      />
    </div>
  )
);

// ——— Bloque “Fecha Compra / Cotización / Factura” como en el diseño
const InlineMeta = memo(() => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 16,
      paddingLeft: 16,
      marginLeft: 6,
      borderLeft: "1px solid #E6EDFF", // barra a la izquierda del grupo
    }}
  >
    {/* Fecha Compra */}
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
        Fecha Compra
      </div>
      <div style={{ fontSize: 13, color: "#6B7280" }}>22 Julio 2025</div>
    </div>

    {/* divisor */}
    <div style={{ width: 1, alignSelf: "stretch", background: "#E6EDFF" }} />

    {/* Cotización */}
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
        Cotización
      </div>
      <div style={{ fontSize: 13, color: "#6B7280" }}>Cot-54321</div>
    </div>

    {/* divisor */}
    <div style={{ width: 1, alignSelf: "stretch", background: "#E6EDFF" }} />

    {/* Factura */}
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#111827" }}>
        Factura
      </div>
      <div style={{ fontSize: 13, color: "#6B7280" }}>INV-0992828222</div>
    </div>
  </div>
));

// ===== Encabezado (título/fecha); aquí va el buscador a la derecha =====
const ListHeader = memo(({ listaNombre, createdAt, rightContent = null }) => (
  <div
    className="list-header"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "0 12px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <h3
        className="list-title"
        style={{ display: "flex", alignItems: "center", gap: 12, margin: 0 }}
      >
        <span style={{ fontSize: 16, fontWeight: 400 }}>
          Gastos de&nbsp;<strong>{listaNombre || "—"}</strong>
        </span>

        {createdAt && (
          <span
            style={{
              fontSize: 9,
              color: "#6b7280",
              background: "#f3f4f6",
              border: "1px solid #e5e7eb",
              borderRadius: 999,
              padding: "4px 10px",
            }}
          >
            Fecha:{" "}
            {new Date(createdAt).toLocaleDateString("es-MX", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
        )}
      </h3>

      <InlineMeta />
    </div>

    {rightContent}
  </div>
));

// ==== Helper para búsquedas sin acentos ====
const buildAccentPattern = (query) => {
  const accentMap = {
    a: "[aáàäâã]",
    e: "[eéèëê]",
    i: "[iíìïî]",
    o: "[oóòöôõ]",
    u: "[uúùüû]",
    n: "[nñ]",
    c: "[cç]",
  };

  const escapeRegex = (ch) => ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  let pattern = "";

  for (const ch of query.toLowerCase()) {
    if (accentMap[ch]) {
      pattern += accentMap[ch];
    } else if (ch === " ") {
      // permite uno o varios espacios
      pattern += "\\s+";
    } else {
      pattern += escapeRegex(ch);
    }
  }

  return pattern;
};

const GastosTableBase = ({
  productos,
  isEditable,
  onChangeCell,
  onRemoveRow,
  isAddingRow,
  newRow,
  onNewRowChange,
  trimLeadingZeros,
  formatCurrency,
  searchTerm,
  showInlineAdd,
  onInlineAddClick,
}) => {
  // Copia interna de productos para evitar parpadeos cuando el padre
  // manda [] de forma momentánea mientras escribes
  const [stableProductos, setStableProductos] = useState(productos || []);

  // Sincroniza cambios "reales" que vengan del padre
  useEffect(() => {
    if (!productos) return;

    setStableProductos((prev) => {
      // 1) Si ambos están vacíos, no toques el estado → no hay re-render
      if (productos.length === 0 && prev.length === 0) {
        return prev;
      }

      // 2) Si es exactamente el mismo array, tampoco cambiamos nada
      if (productos === prev) {
        return prev;
      }

      // 3) Si la longitud y las referencias de cada fila son iguales,
      //    consideramos que no hay cambios reales
      if (
        productos.length === prev.length &&
        productos.every((row, idx) => row === prev[idx])
      ) {
        return prev;
      }

      // 4) En cualquier otro caso sí sincronizamos con lo que venga del padre
      return productos;
    });
  }, [productos]);

  // === handlers locales para escribir fluido ===
  const handleLocalChange = useCallback(
    (rowIndex, field, rawValue) => {
      const value =
        field === "cantidad" || field === "precioUnitario"
          ? trimLeadingZeros(rawValue)
          : rawValue;

      setStableProductos((prev) =>
        prev.map((row, idx) =>
          idx === rowIndex ? { ...row, [field]: value } : row
        )
      );
    },
    [trimLeadingZeros]
  );

  const handleBlur = useCallback(
    (rowIndex, field, value) => {
      // Solo aquí avisamos al padre → menos renders pesados
      onChangeCell?.(rowIndex, field, value);
    },
    [onChangeCell]
  );

  const rows = stableProductos || [];

  // Resalta coincidencias con el término de búsqueda (como BBVA amarillo)
  const highlightMatch = useCallback(
    (text) => {
      const query = (searchTerm || "").trim();
      if (!query) return text;

      const str = String(text ?? "");
      const pattern = buildAccentPattern(query);

      // regex global para hacer el split y conservar coincidencias
      const regexGlobal = new RegExp(`(${pattern})`, "gi");
      // regex sin 'g' para probar cada parte
      const regexSingle = new RegExp(pattern, "i");

      const parts = str.split(regexGlobal);
      if (parts.length === 1) return str; // no hubo match

      return parts.map((part, idx) => {
        if (!part) return null;

        if (regexSingle.test(part)) {
          // esto es una coincidencia → la pintamos de amarillo
          return (
            <span
              key={idx}
              style={{
                backgroundColor: "#fff59d", // amarillo marcador
                padding: "0 1px",
              }}
            >
              {part}
            </span>
          );
        }

        // texto normal, sin resaltar
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      });
    },
    [searchTerm]
  );

  // Totales SIEMPRE calculados sobre la fuente estable
  const sub = useMemo(
    () =>
      (stableProductos || []).reduce(
        (s, g) =>
          s + (Number(g.cantidad) || 0) * (Number(g.precioUnitario) || 0),
        0
      ),
    [stableProductos]
  );

  const iva = sub * 0.16;
  const total = sub + iva;

  // --- evitar flash de fila duplicada al guardar nueva partida ---
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;

  const isDuplicateNewRow =
    isAddingRow &&
    lastRow &&
    newRow &&
    lastRow.descripcion === newRow.descripcion &&
    lastRow.noParte === newRow.noParte &&
    String(lastRow.cantidad ?? "") === String(newRow.cantidad ?? "") &&
    String(lastRow.precioUnitario ?? "") ===
      String(newRow.precioUnitario ?? "");

  // solo mostramos la fila de "nueva partida" si NO está ya en la tabla
  const showNewRow = isAddingRow && !isDuplicateNewRow;

  const canShowInlineAdd = !isAddingRow && (rows?.length || 0) > 0;

  const cellBaseStyle = {
    padding: "12px 10px", // ← aquí subes/bajas la altura
    height: 44, // opcional, solo para asegurar la fila más alta
    verticalAlign: "middle",
  };

  const cellInputStyle = {
    width: "100%",
    height: 34, // ← aquí controlas la altura del recuadro
    padding: "6px 10px", // ← espacio interno del input
    borderRadius: 4,
    border: "1px solid #E6EDFF",
    boxSizing: "border-box",
    fontSize: 13,
  };

  return (
    <>
      <table className="detalles-table">
        <thead>
          <tr>
            <th style={{ ...cellBaseStyle, textAlign: "center" }}>#</th>
            <th style={cellBaseStyle}>Descripción</th>
            <th style={cellBaseStyle}>No. Parte</th>
            <th style={{ ...cellBaseStyle, textAlign: "center" }}>Cantidad</th>
            <th style={cellBaseStyle}>Precio unit.</th>
            <th style={{ ...cellBaseStyle, textAlign: "center" }}>Subtotal</th>
            <th style={cellBaseStyle} />
          </tr>
        </thead>
        <tbody>
          {/* Mensaje cuando de verdad no hay nada. Lo mostramos aunque esté en modo edición, solo lo ocultamos si se está agregando una nueva fila */}
          {rows.length === 0 && !isAddingRow && (
            <tr>
              <td />
              <td colSpan={6} style={{ padding: "14px 8px" }}>
                <span style={{ opacity: 0.6 }}>
                  Sin productos. Usa el botón "Registrar Gasto" para agregar
                  uno.
                </span>
              </td>
            </tr>
          )}

          {/* Filas existentes */}
          {rows.map((p, i) => (
            <tr key={p.id ?? i}>
              {/* # */}
              <td style={cellBaseStyle}>{i + 1}</td>

              {/* DESCRIPCIÓN */}
              <td style={cellBaseStyle}>
                {isEditable ? (
                  <input
                    style={cellInputStyle}
                    value={p.descripcion}
                    onChange={(e) =>
                      handleLocalChange(i, "descripcion", e.target.value)
                    }
                    onBlur={(e) => handleBlur(i, "descripcion", e.target.value)}
                  />
                ) : (
                  <span
                    className="gasto-cell-text"
                    style={{
                      display: "block",
                      textAlign: "justify",
                    }}
                  >
                    {highlightMatch(p.descripcion)}
                  </span>
                )}
              </td>

              {/* NO. PARTE */}
              <td style={cellBaseStyle}>
                {isEditable ? (
                  <input
                    style={cellInputStyle}
                    value={p.noParte}
                    onChange={(e) =>
                      handleLocalChange(i, "noParte", e.target.value)
                    }
                    onBlur={(e) => handleBlur(i, "noParte", e.target.value)}
                  />
                ) : (
                  <span className="gasto-cell-text">
                    {highlightMatch(p.noParte)}
                  </span>
                )}
              </td>

              {/* CANTIDAD */}
              <td style={{ ...cellBaseStyle, textAlign: "center" }}>
                {isEditable ? (
                  <input
                    style={cellInputStyle}
                    type="number"
                    value={p.cantidad}
                    onChange={(e) =>
                      handleLocalChange(i, "cantidad", e.target.value)
                    }
                    onBlur={(e) =>
                      handleBlur(
                        i,
                        "cantidad",
                        trimLeadingZeros(e.target.value)
                      )
                    }
                  />
                ) : (
                  <span
                    className="gasto-cell-text"
                    style={{ textAlign: "center", display: "block" }}
                  >
                    {p.cantidad}
                  </span>
                )}
              </td>

              {/* PRECIO UNITARIO */}
              <td style={{ ...cellBaseStyle, textAlign: "center" }}>
                {isEditable ? (
                  <input
                    type="number"
                    value={p.precioUnitario}
                    onChange={(e) =>
                      handleLocalChange(i, "precioUnitario", e.target.value)
                    }
                    onBlur={(e) =>
                      handleBlur(
                        i,
                        "precioUnitario",
                        trimLeadingZeros(e.target.value)
                      )
                    }
                    style={{ ...cellInputStyle, textAlign: "center" }}
                  />
                ) : (
                  <span
                    className="gasto-cell-text"
                    style={{
                      display: "block",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {highlightMatch(
                      Number(p.precioUnitario || 0).toLocaleString("es-MX", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 2,
                      })
                    )}
                  </span>
                )}
              </td>

              {/* SUBTOTAL */}
              <td
                className="text-right"
                style={{ ...cellBaseStyle, textAlign: "right" }}
              >
                {formatCurrency(
                  (Number(p.cantidad) || 0) * (Number(p.precioUnitario) || 0)
                )}
              </td>

              {/* BASURA SOLO EN MODO EDICIÓN */}
              <td style={cellBaseStyle}>
                {isEditable && (
                  <i
                    className="bi bi-trash-fill trash-icon"
                    onClick={() => onRemoveRow(i)}
                  />
                )}
              </td>
            </tr>
          ))}

          {/* Fila de nueva partida (si se está agregando) */}
          {showNewRow && (
            <tr>
              <td style={{ textAlign: "center" }}>{rows.length + 1}</td>
              <td>
                <input
                  style={cellInputStyle}
                  value={newRow.descripcion}
                  onChange={(e) =>
                    onNewRowChange({
                      ...newRow,
                      descripcion: e.target.value,
                    })
                  }
                />
              </td>
              <td>
                <input
                  style={cellInputStyle}
                  value={newRow.noParte}
                  onChange={(e) =>
                    onNewRowChange({ ...newRow, noParte: e.target.value })
                  }
                />
              </td>
              <td>
                <input
                  style={{ ...cellInputStyle, textAlign: "right" }}
                  value={newRow.cantidad}
                  onChange={(e) =>
                    onNewRowChange({
                      ...newRow,
                      cantidad: trimLeadingZeros(e.target.value),
                    })
                  }
                />
              </td>
              <td>
                <input
                  style={{ ...cellInputStyle, textAlign: "right" }}
                  value={newRow.precioUnitario}
                  onChange={(e) =>
                    onNewRowChange({
                      ...newRow,
                      precioUnitario: trimLeadingZeros(e.target.value),
                    })
                  }
                />
              </td>
              <td style={{ textAlign: "right", fontWeight: 500 }}>
                {formatCurrency(
                  (parseFloat(newRow.cantidad) || 0) *
                    (parseFloat(newRow.precioUnitario) || 0)
                )}
              </td>
              <td />
            </tr>
          )}

          {/* Botón inline "Añadir gasto" AHORA SÍ ABAJO de la última partida */}
          {canShowInlineAdd && (
            <tr>
              <td />
              <td colSpan={6} style={{ padding: "10px 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    type="button"
                    onClick={onInlineAddClick}
                    style={{
                      background: "#3F9BFC",
                      border: "1px solid #3F9BFC",
                      color: "#fff",
                      padding: "8px 14px",
                      borderRadius: 6,
                      fontWeight: 700,
                      fontSize: 14,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      lineHeight: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#2F8AF5";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#3F9BFC";
                    }}
                  >
                    <span
                      style={{ fontSize: 18, lineHeight: 1, marginTop: -1 }}
                    >
                      +
                    </span>
                    Añadir partida
                  </button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Resumen fijo, pegado a la tabla, usando los mismos datos estables */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <div style={{ minWidth: 260 }}>
          {/* Subtotal */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 16px",
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: "#6B7280",
              }}
            >
              Subtotal
            </span>
            <span
              style={{
                fontSize: 14,
                color: "#111827",
              }}
            >
              {formatCurrency(sub)}
            </span>
          </div>

          {/* IVA */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "0 16px",
              marginTop: 10,
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: "#6B7280",
              }}
            >
              IVA (16%)
            </span>
            <span
              style={{
                fontSize: 14,
                color: "#111827",
              }}
            >
              {formatCurrency(iva)}
            </span>
          </div>

          {/* Total */}
          <div
            style={{
              marginTop: 12,
              background: "#FAFAFD",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            <span style={{ fontSize: 15, color: "#111827" }}>Total MXN</span>
            <span style={{ fontSize: 16, color: "#111827" }}>
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

// Memoizamos la versión base
const GastosTable = memo(GastosTableBase);

// --- Barra de pestañas ---
const TabsBar = memo(
  ({ labels, counts, activeIndex, onChange, onAdd, onMenuClick }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        borderBottom: "1px solid #E6EDFF",
        marginBottom: 12,
        paddingBottom: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center", // antes "flex-end"
          gap: 0,
          marginBottom: 0,
        }}
      >
        {labels.map((label, i) => {
          const active = i === activeIndex;

          return (
            <button
              key={i}
              onClick={() => onChange(i)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "transparent",
                border: "none",
                padding: "6px 3px 12px",
                cursor: "pointer",
                color: active ? "#111827" : "#6b7280",
                borderBottom: active
                  ? "3px solid #2563eb"
                  : "3px solid transparent",
                fontWeight: 600,
                whiteSpace: "nowrap",
                flex: "0 0 auto",
                lineHeight: 1.1,
              }}
              title={label}
            >
              {/* contador PRIMERO */}
              <span
                style={{
                  fontSize: 12,
                  color: active ? "#2563eb" : "#6b7280",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 999,
                  padding: "1px 5px",
                  lineHeight: 1,
                }}
              >
                {counts[i] ?? 0}
              </span>

              {/* label DESPUÉS */}
              <span>{label}</span>

              {/* ⋮ al FINAL, solo en pestañas extra */}
              {i > 0 && (
                <button
                  type="button"
                  title="Opciones"
                  onClick={(e) => onMenuClick?.(i, e)}
                  style={{
                    marginLeft: -2,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: "1px solid transparent",
                    background: "transparent",
                    cursor: "pointer",
                    color: active ? "#111827" : "#6b7280",
                    padding: 0,
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#f3f4f6";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                >
                  <i
                    className="bi bi-three-dots-vertical"
                    style={{ fontSize: 12, lineHeight: 1, display: "block" }}
                  />
                </button>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={onAdd}
        title="Nueva tabla"
        style={{
          marginLeft: 4,
          transform: "translateY(-4.5px)",
          background: "#eff6ff",
          color: "#1d4ed8",
          border: "1px solid #bfdbfe",
          borderRadius: 8,
          padding: "6px 10px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        <i className="bi bi-plus-lg" /> Nueva
      </button>
    </div>
  )
);

// --- Encabezado superior con Total + acciones ---
const FolderTotalHeader = memo(
  ({ title, totalText, currencyCode, actions }) => (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          paddingTop: 4,
          paddingBottom: 6,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 20,
            minHeight: 40,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: 0.2 }}>
            {title || "—"}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              paddingLeft: 18,
              marginLeft: 4,
              borderLeft: "1px solid #E6EDFF",
              minHeight: 40,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{totalText}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                {currencyCode}
              </span>
            </div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>Total Gastos</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {actions}
        </div>
      </div>

      <div style={{ borderBottom: "1px solid #E6EDFF", marginBottom: 12 }} />
    </>
  )
);

// ===== Principal =====
function ListaGastos({
  listaNombre,
  isEditable,
  onToggleEdit,
  productos,
  onChangeCell,
  onRemoveRow,
  isAddingRow,
  newRow,
  onNewRowChange,
  trimLeadingZeros,
  formatCurrency,
  showResumen,
  canShowFab,
  onFabClick,
  onGuardarNuevaFila,
  onCancelarNuevaFila,
  currencyCode,
}) {
  const [extraTables, setExtraTables] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [, setInlineAddTab] = useState(null);
  const [ctxMenu, setCtxMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    tabIndex: null, // índice de pestaña (0=principal, 1..n=extras)
  });

  const pathKey = useMemo(
    () => window.location.pathname.replace(/\/+$/, ""),
    []
  );
  const listaKey = useMemo(() => slug(listaNombre), [listaNombre]);
  const persistKey = useMemo(
    () => `gastos-extra:${pathKey}:${listaKey}`,
    [pathKey, listaKey]
  );

  const [loadedKey, setLoadedKey] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(persistKey);
      setExtraTables(raw ? JSON.parse(raw) : []);
      setActiveTab(0);
    } catch {
      setExtraTables([]);
      setActiveTab(0);
    } finally {
      setLoadedKey(persistKey);
    }
  }, [persistKey]);

  useEffect(() => {
    if (loadedKey !== persistKey) return;
    try {
      localStorage.setItem(persistKey, JSON.stringify(extraTables));
    } catch {}
  }, [persistKey, loadedKey, extraTables]);

  useEffect(() => {
    if (!ctxMenu.open) return;

    const close = () =>
      setCtxMenu((m) => ({ ...m, open: false, tabIndex: null }));

    const onKey = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("click", close);
    window.addEventListener("blur", close);
    window.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("blur", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu.open]);

  useEffect(() => {
    const maxIndex = extraTables?.length || 0;
    if (activeTab > maxIndex) setActiveTab(maxIndex);
  }, [extraTables, activeTab]);

  const handleTabMenuClick = useCallback((tabIndex, e) => {
    e.preventDefault();
    e.stopPropagation(); // para que NO cambie de tab al dar clic en ⋮

    if (tabIndex === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setCtxMenu({
      open: true,
      x: rect.left,
      y: rect.bottom + 6,
      tabIndex,
    });
  }, []);

  const createNewTable = useCallback(() => {
    const nowIso = new Date().toISOString();

    const baseTab = {
      id:
        (typeof crypto !== "undefined" &&
          crypto?.randomUUID &&
          crypto.randomUUID()) ||
        `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      createdAt: nowIso,
      productos: [],
      isEditable: false,
      isAddingRow: false,
      newRow: {
        descripcion: "",
        noParte: "",
        cantidad: "",
        precioUnitario: "",
      },
    };

    setExtraTables((prev) => {
      const nextNumber = prev.length + 2; // Tabla 2, Tabla 3...
      const newTab = { ...baseTab, name: `Tabla ${nextNumber}` };

      const next = [...prev, newTab];
      setActiveTab(prev.length + 1); // pestaña nueva activa (0 es principal)
      return next;
    });
  }, []);

  const toggleEditExtra = useCallback((idx) => {
    setExtraTables((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, isEditable: !t.isEditable } : t))
    );
  }, []);

  const changeCellExtra = useCallback((idx, rowIdx, field, value) => {
    setExtraTables((prev) =>
      prev.map((t, i) =>
        i === idx
          ? {
              ...t,
              productos: t.productos.map((p, j) =>
                j === rowIdx ? { ...p, [field]: value } : p
              ),
            }
          : t
      )
    );
  }, []);

  const removeRowExtra = useCallback((idx, rowIdx) => {
    setExtraTables((prev) =>
      prev.map((t, i) =>
        i === idx
          ? {
              ...t,
              productos: t.productos.filter((_, j) => j !== rowIdx),
            }
          : t
      )
    );
  }, []);

  const fabClickExtra = useCallback((idx) => {
    setExtraTables((prev) =>
      prev.map((t, i) =>
        i === idx
          ? {
              ...t,
              isAddingRow: true,
              newRow: {
                descripcion: "",
                noParte: "",
                cantidad: "",
                precioUnitario: "",
              },
            }
          : t
      )
    );
  }, []);

  const onNewRowChangeExtra = useCallback((idx, row) => {
    setExtraTables((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, newRow: row } : t))
    );
  }, []);

  const guardarNuevaFilaExtra = useCallback((idx) => {
    setExtraTables((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const nr = t.newRow || {};
        const row = {
          id: Date.now(),
          descripcion: nr.descripcion || "",
          noParte: nr.noParte || "",
          cantidad: parseFloat(nr.cantidad) || 0,
          precioUnitario: parseFloat(nr.precioUnitario) || 0,
        };
        return {
          ...t,
          productos: [...t.productos, row],
          isAddingRow: false,
          newRow: {
            descripcion: "",
            noParte: "",
            cantidad: "",
            precioUnitario: "",
          },
        };
      })
    );
  }, []);

  const cancelarNuevaFilaExtra = useCallback((idx) => {
    setExtraTables((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, isAddingRow: false } : t))
    );
  }, []);

  // --- wrappers para limpiar inlineAddTab al terminar de agregar partida ---
  const handleGuardarNuevaFilaMain = useCallback(() => {
    if (onGuardarNuevaFila) onGuardarNuevaFila();
    // después de guardar en la pestaña principal,
    // mostramos el botón "Añadir partida"
    setInlineAddTab(0);
  }, [onGuardarNuevaFila]);

  const handleCancelarNuevaFilaMain = useCallback(() => {
    if (onCancelarNuevaFila) onCancelarNuevaFila();
    setInlineAddTab(null);
  }, [onCancelarNuevaFila]);

  const handleGuardarNuevaFilaExtraClick = useCallback(
    (idx) => {
      guardarNuevaFilaExtra(idx);

      // pestañas extra: el índice de tab es idx + 1
      setInlineAddTab(idx + 1);
    },
    [guardarNuevaFilaExtra]
  );

  const handleCancelarNuevaFilaExtraClick = useCallback(
    (idx) => {
      cancelarNuevaFilaExtra(idx);
      setInlineAddTab(null);
    },
    [cancelarNuevaFilaExtra]
  );

  const handleRegistrarGasto = useCallback(() => {
    // al empezar a registrar, ocultamos el botón inline (si estaba)
    setInlineAddTab(null);

    // si ya estás agregando fila, no reinicies (evita borrar lo escrito)
    const isCurrentlyAdding =
      activeTab === 0 ? isAddingRow : !!extraTables[activeTab - 1]?.isAddingRow;

    if (isCurrentlyAdding) return;

    // abrimos la fila editable
    if (activeTab === 0) {
      onFabClick?.();
    } else {
      fabClickExtra(activeTab - 1);
    }
  }, [activeTab, isAddingRow, extraTables, onFabClick, fabClickExtra]);

  const handleInlineAddClick = useCallback(() => {
    if (activeTab === 0) {
      onFabClick?.();
    } else {
      fabClickExtra(activeTab - 1);
    }
  }, [activeTab, onFabClick, fabClickExtra]);

  // ====== TOTALES POR PESTAÑA (no globales) ======

  // subtotal de la pestaña principal
  const mainSubtotal = useMemo(
    () =>
      (productos || []).reduce(
        (s, g) =>
          s + (Number(g.cantidad) || 0) * (Number(g.precioUnitario) || 0),
        0
      ),
    [productos]
  );

  // subtotales de cada tabla extra
  const extrasSubtotals = useMemo(
    () =>
      (extraTables || []).map((t) =>
        (t.productos || []).reduce(
          (s, g) =>
            s + (Number(g.cantidad) || 0) * (Number(g.precioUnitario) || 0),
          0
        )
      ),
    [extraTables]
  );

  // subtotal GLOBAL (suma de todas las pestañas: principal + extras)
  const totalGastosSubtotal = useMemo(() => {
    const extrasTotal = (extrasSubtotals || []).reduce(
      (s, sub) => s + (Number(sub) || 0),
      0
    );
    return mainSubtotal + extrasTotal;
  }, [mainSubtotal, extrasSubtotals]);

  const inferredCurrencyCode = useMemo(() => {
    if (currencyCode && typeof currencyCode === "string")
      return currencyCode.toUpperCase();
    try {
      const sample = String(formatCurrency?.(1234.56) ?? "");
      if (/\bmxn\b/i.test(sample)) return "MXN";
      if (/\busd\b/i.test(sample) || /US\$/i.test(sample)) return "USD";
      if (/€|eur/i.test(sample)) return "EUR";
      if (/pen|s\/\./i.test(sample)) return "PEN";
    } catch {}
    return "MXN";
  }, [currencyCode, formatCurrency]);

  // Total GLOBAL para mostrar en "Total Gastos"
  const totalGastosText = useMemo(
    () => formatCurrency(totalGastosSubtotal * 1.16),
    [totalGastosSubtotal, formatCurrency]
  );

  const tabLabels = useMemo(
    () => [
      listaNombre || "Principal",
      ...extraTables.map((t, i) => t.name?.trim() || `Tabla ${i + 2}`),
    ],
    [listaNombre, extraTables]
  );

  const tabCounts = useMemo(
    () => [
      (productos || []).length,
      ...extraTables.map((t) => (t.productos || []).length),
    ],
    [productos, extraTables]
  );

  const handleTopToggleEdit = useCallback(() => {
    if (activeTab === 0) onToggleEdit?.();
    else toggleEditExtra(activeTab - 1);
  }, [activeTab, onToggleEdit, toggleEditExtra]);

  const handleTopPrint = useCallback(() => window.print(), []);

  const handleTabChange = useCallback(
    (nextIndex) => {
      // 1) Apagar edición en la pestaña que dejamos
      if (activeTab === 0) {
        // venimos de la pestaña principal
        if (isEditable) {
          onToggleEdit?.(); // la ponemos en no editable
        }
      } else {
        // venimos de una tabla extra
        const leavingIdx = activeTab - 1;
        setExtraTables((prev) =>
          prev.map((t, i) =>
            i === leavingIdx ? { ...t, isEditable: false } : t
          )
        );
      }

      // 2) Asegurar que la pestaña a la que llegamos también esté en no editable
      if (nextIndex > 0) {
        const destIdx = nextIndex - 1;
        setExtraTables((prev) =>
          prev.map((t, i) => (i === destIdx ? { ...t, isEditable: false } : t))
        );
      } else {
        // pestaña principal: nos aseguramos de que esté en no editable
        if (isEditable) {
          onToggleEdit?.();
        }
      }

      // apagamos el inline add al cambiar de pestaña
      setInlineAddTab(null);

      // 3) Finalmente cambiamos la pestaña activa
      setActiveTab(nextIndex);
    },
    [activeTab, isEditable, onToggleEdit]
  );

  const renameExtraTable = useCallback((extraIdx, newName) => {
    const clean = (newName || "").trim();
    if (!clean) return;

    setExtraTables((prev) =>
      prev.map((t, i) => (i === extraIdx ? { ...t, name: clean } : t))
    );
  }, []);

  const deleteExtraTable = useCallback(
    (extraIdx) => {
      setExtraTables((prev) => {
        const next = prev.filter((_, i) => i !== extraIdx);

        // si borras la pestaña activa, brinca a la anterior
        const overallIndex = extraIdx + 1; // 1..n en tabs
        if (activeTab === overallIndex) {
          setActiveTab(Math.max(0, overallIndex - 1));
        }

        return next;
      });
    },
    [activeTab]
  );

  // Render de pestaña activa
  const renderActiveTab = () => {
    // Pestaña principal
    if (activeTab === 0) {
      const isEditingExisting = isEditable && !isAddingRow;

      return (
        <>
          <ListHeader
            listaNombre={listaNombre}
            rightContent={
              <SearchBox
                placeholder="Buscar artículo"
                value={searchTerm}
                onChange={setSearchTerm}
              />
            }
          />

          <GastosTable
            key="gastos-main"
            productos={productos}
            isEditable={isEditable}
            onChangeCell={onChangeCell}
            onRemoveRow={onRemoveRow}
            isAddingRow={isAddingRow}
            newRow={newRow}
            onNewRowChange={onNewRowChange}
            trimLeadingZeros={trimLeadingZeros}
            formatCurrency={formatCurrency}
            searchTerm={searchTerm}
            showInlineAdd={true}
            onInlineAddClick={handleInlineAddClick}
          />

          {/* Botones de Guardar / Cancelar SOLO cuando vienes de Editar, no de Registrar gasto */}
          {isEditingExisting && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                }}
              >
                {/* Izquierda: Cancelar */}
                <button
                  style={{
                    backgroundColor: "#e74c3c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                  onClick={handleTopToggleEdit}
                >
                  Cancelar
                </button>

                {/* Derecha: Guardar */}
                <button
                  style={{
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                  onClick={handleTopToggleEdit}
                >
                  Guardar
                </button>
              </div>
            </div>
          )}

          {isAddingRow && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                }}
              >
                {/* Izquierda: Cancelar nueva partida */}
                <button
                  style={{
                    backgroundColor: "#e74c3c",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                  onClick={handleCancelarNuevaFilaMain}
                >
                  Cancelar
                </button>

                {/* Derecha: Guardar nueva partida */}
                <button
                  style={{
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.5rem 1rem",
                    cursor: "pointer",
                  }}
                  onClick={handleGuardarNuevaFilaMain}
                >
                  Guardar
                </button>
              </div>
            </div>
          )}
        </>
      );
    }

    // Pestañas extra (Tabla 2, Tabla 3, ...)
    const idx = activeTab - 1;
    const tab = extraTables[idx] || {};

    const isEditingExistingExtra = !!tab.isEditable && !tab.isAddingRow;

    return (
      <>
        <ListHeader
          listaNombre={`${listaNombre} — Tabla ${idx + 2}`}
          createdAt={tab.createdAt}
          rightContent={
            <SearchBox
              placeholder="Buscar artículo"
              value={searchTerm}
              onChange={setSearchTerm}
            />
          }
        />

        <GastosTable
          key={`gastos-extra-${tab.id || idx}`}
          productos={tab.productos || []}
          isEditable={tab.isEditable}
          onChangeCell={(rowIdx, field, value) =>
            changeCellExtra(idx, rowIdx, field, value)
          }
          onRemoveRow={(rowIdx) => removeRowExtra(idx, rowIdx)}
          isAddingRow={tab.isAddingRow}
          newRow={tab.newRow}
          onNewRowChange={(row) => onNewRowChangeExtra(idx, row)}
          trimLeadingZeros={trimLeadingZeros}
          formatCurrency={formatCurrency}
          searchTerm={searchTerm}
          showInlineAdd={true}
          onInlineAddClick={handleInlineAddClick}
        />

        {isEditingExistingExtra && (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
              }}
            >
              <button
                style={{
                  backgroundColor: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                }}
                onClick={handleTopToggleEdit}
              >
                Cancelar
              </button>
              <button
                style={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                }}
                onClick={handleTopToggleEdit}
              >
                Guardar
              </button>
            </div>
          </div>
        )}

        {tab.isAddingRow && (
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
              }}
            >
              <button
                style={{
                  backgroundColor: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                }}
                onClick={() => handleCancelarNuevaFilaExtraClick(idx)}
              >
                Cancelar
              </button>
              <button
                style={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                }}
                onClick={() => handleGuardarNuevaFilaExtraClick(idx)}
              >
                Guardar
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className="gastos-main"
      style={{
        flex: 1,
        display: "flex",
        paddingLeft: 28, // antes 8
        paddingRight: 8,
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: 0,
        marginBottom: 0,
        boxSizing: "border-box",
        alignItems: "stretch",
      }}
    >
      <div
        className="list-view"
        style={{
          border: "1px solid #E6EDFF",
          borderRadius: 6,
          background: "#FFFFFF",
          padding: "16px 24px",
          width: "100%",
          boxSizing: "border-box",
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        {/* Encabezado con Total (izquierda) + Acciones (derecha) */}
        <FolderTotalHeader
          title={listaNombre}
          totalText={totalGastosText}
          currencyCode={inferredCurrencyCode}
          actions={
            <>
              <RectBtn icon="bi-pencil-square" onClick={handleTopToggleEdit}>
                Editar
              </RectBtn>
              <RectBtn icon="bi-printer" onClick={handleTopPrint}>
                Imprimir
              </RectBtn>

              <PrimaryBtn onClick={handleRegistrarGasto}>
                <span
                  style={{
                    fontSize: 18,
                    lineHeight: 1,
                    marginRight: 2,
                    marginTop: -1,
                  }}
                >
                  +
                </span>
                Registrar Gasto
              </PrimaryBtn>
            </>
          }
        />

        {/* Pestañas */}
        <TabsBar
          labels={tabLabels}
          counts={tabCounts}
          activeIndex={activeTab}
          onChange={handleTabChange}
          onAdd={createNewTable}
          onMenuClick={handleTabMenuClick}
        />

        {/* Menú de opciones (⋮ en pestañas extra) */}
        {ctxMenu.open && ctxMenu.tabIndex > 0 && (
          <div
            style={{
              position: "fixed",
              top: ctxMenu.y,
              left: ctxMenu.x,
              background: "#fff",
              border: "1px solid #E6EDFF",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              padding: 6,
              zIndex: 9999,
              minWidth: 180,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cambiar nombre */}
            <button
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                textAlign: "left",
                padding: "8px 10px",
                cursor: "pointer",
                fontSize: 14,
              }}
              onClick={() => {
                const overallIndex = ctxMenu.tabIndex; // 1..n
                const extraIdx = overallIndex - 1; // 0..n-1
                const current =
                  extraTables[extraIdx]?.name || `Tabla ${extraIdx + 2}`;

                setCtxMenu((m) => ({ ...m, open: false }));

                const newName = window.prompt(
                  "Nuevo nombre para la pestaña:",
                  current
                );

                if (newName !== null) {
                  renameExtraTable(extraIdx, newName);
                }
              }}
            >
              Cambiar nombre
            </button>

            <div
              style={{ height: 1, background: "#E6EDFF", margin: "4px 0" }}
            />

            {/* Eliminar */}
            <button
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                textAlign: "left",
                padding: "8px 10px",
                cursor: "pointer",
                fontSize: 14,
                color: "#dc2626",
              }}
              onClick={() => {
                const overallIndex = ctxMenu.tabIndex;
                const extraIdx = overallIndex - 1;

                setCtxMenu((m) => ({ ...m, open: false }));

                const ok = window.confirm(
                  "¿Seguro que quieres eliminar esta pestaña y sus gastos?"
                );
                if (ok) {
                  deleteExtraTable(extraIdx);
                }
              }}
            >
              Eliminar pestaña
            </button>
          </div>
        )}

        {/* Contenido de la pestaña activa */}
        {renderActiveTab()}
      </div>
    </div>
  );
}

export default memo(ListaGastos);
